import { DateTime } from "luxon"
import { useMemo } from "react"
import { getEchartsMax } from "@/lib/utils"

export type WaterConsumptionDataPoint = {
  type: string
  index: number
  time: DateTime
}

export type ChartData = {
  intervalFlowData: [number, number][]
  totalFlowChartData: [number, number, number][]
  maxIntervalFlow: number
  maxTotalFlow: number
}

type Item = {
  type: string
  index: number
  time: number
}

const mapToItem = (item: WaterConsumptionDataPoint): Item => ({
  index: item.index,
  type: item.type,
  time: item.time.setZone().toMillis(),
})

export const useChartData = (
  intervalFlowData: WaterConsumptionDataPoint[],
  viewMode: "hourly" | "daily" | "weekly",
  selectedDate?: DateTime,
  selectedMonth?: DateTime,
  selectedWeek?: DateTime,
): ChartData => {
  const filter = useMemo<(item: WaterConsumptionDataPoint) => boolean>(() => {
    let ref: DateTime = DateTime.now()
    let startTime: DateTime
    let endTime: DateTime
    switch (viewMode) {
      case "hourly": {
        ref = (selectedDate ?? ref).setZone()
        startTime = ref.startOf("day").minus({ minutes: 30 })
        endTime = ref.plus({ day: 1 }).startOf("day")
        break
      }
      case "daily": {
        ref = (selectedMonth ?? ref).setZone()
        startTime = ref.startOf("month").startOf("day")
        endTime = ref.plus({ month: 1 }).startOf("month").startOf("day")
        break
      }
      case "weekly": {
        ref = (selectedWeek ?? ref).setZone()
        startTime = ref.startOf("week").startOf("day")
        endTime = ref.plus({ weeks: 1 }).startOf("week").startOf("day")
        break
      }
    }

    return (item) => {
      return startTime <= item.time && item.time <= endTime
    }
  }, [selectedDate, selectedMonth, selectedWeek, viewMode])

  const processedIntervalFlowData = useMemo(() => {
    const format = "HH dd MM yyyy"
    const items: Item[] = []
    const data: Map<string, number> = new Map()

    let startOfRangeTime: DateTime
    let endOfRangeTime: DateTime

    switch (viewMode) {
      case "hourly": {
        const time = selectedDate!.setZone()
        startOfRangeTime = time.startOf("day").startOf("hour")
        endOfRangeTime = time.plus({ day: 1 }).startOf("day")
        break
      }
      case "daily": {
        const time = selectedMonth!.setZone()
        startOfRangeTime = time.startOf("month").startOf("day")
        endOfRangeTime = time.plus({ month: 1 }).startOf("month").startOf("day")
        break
      }
      case "weekly": {
        const time = selectedWeek!.setZone()
        startOfRangeTime = time.startOf("week").startOf("day")
        endOfRangeTime = time.plus({ weeks: 1 }).startOf("week").startOf("day")
        break
      }
    }

    for (let i = startOfRangeTime.set({}); i <= endOfRangeTime; i = i.plus({ hour: 1 })) {
      data.set(i.toFormat(format), 0)
    }

    intervalFlowData.filter(filter).forEach((item) => {
      const timeKey = item.time.minute > 0 ? item.time.plus({ hour: 1 }) : item.time
      const key = timeKey.toFormat(format)
      const currentValue = data.get(key) ?? 0
      if (data.has(key)) data.set(key, currentValue + item.index)
    })

    data.forEach((value, key) => {
      const time = DateTime.fromFormat(key, format)
      items.push(
        mapToItem({
          type: "interval",
          index: value,
          time,
        }),
      )
    })

    return items.map((i) => [i.time, i.index] as [number, number])
  }, [filter, intervalFlowData, selectedDate, selectedMonth, selectedWeek, viewMode])

  const totalFlowChartData = useMemo(() => {
    let items: Item[]

    if (viewMode === "weekly") {
      const format = "dd MM yyyy"
      const days: Map<string, number> = new Map()

      if (selectedWeek == null) return []

      const startOfWeek = selectedWeek.startOf("week").startOf("day")
      const endOfWeek = startOfWeek.plus({ week: 1 }).startOf("week").startOf("day")
      const dayGroups = new Map<string, number>()

      intervalFlowData.forEach((item) => {
        const isStartOfDay =
          item.time.diff(item.time.startOf("day"), "millisecond").milliseconds === 0
        const thisDay = item.time.startOf("day")
        const dayKey = (isStartOfDay ? thisDay.minus({ day: 1 }) : thisDay).toFormat(format)
        if (!dayGroups.has(dayKey)) {
          dayGroups.set(dayKey, 0)
        }
        const currentAmount = dayGroups.get(dayKey) ?? 0
        dayGroups.set(dayKey, currentAmount + item.index)
      })

      for (let i = startOfWeek; i < endOfWeek; i = i.plus({ days: 1 })) {
        const key = i.toFormat(format)
        days.set(key, dayGroups.get(key) ?? 0)
      }

      items = Array.from(
        Array.from(days.entries()).map(([key, value]) => {
          return mapToItem({
            type: "total",
            index: value,
            time: DateTime.fromFormat(key, format),
          })
        }),
      )
    } else if (viewMode === "daily") {
      const format = "dd MM yyyy"
      const days: Map<string, number> = new Map()

      const startOfMonth = selectedMonth!.startOf("month").startOf("day")
      const endOfMonth = startOfMonth.endOf("month").endOf("day")
      const dayGroups = new Map<string, number>()

      intervalFlowData.forEach((item) => {
        const startOfThisDay = item.time.startOf("day")
        const isStartOfDay = item.time.diff(startOfThisDay, "millisecond").milliseconds === 0
        const dayKey = (isStartOfDay ? startOfThisDay.minus({ day: 1 }) : startOfThisDay).toFormat(
          format,
        )

        if (!dayGroups.has(dayKey)) {
          dayGroups.set(dayKey, 0)
        }

        const currentAmount = dayGroups.get(dayKey) ?? 0
        dayGroups.set(dayKey, currentAmount + item.index)
      })

      for (let i = startOfMonth; i <= endOfMonth; i = i.plus({ days: 1 })) {
        const key = i.toFormat(format)
        days.set(key, dayGroups.get(key) ?? 0)
      }

      items = Array.from(
        Array.from(days.entries()).map(([key, value]) => {
          return mapToItem({
            type: "total",
            index: value,
            time: DateTime.fromFormat(key, format),
          })
        }),
      )
    } else {
      if (selectedDate == null) return []

      const time = selectedDate
      const nextDay = time.plus({ days: 1 })
      const allIntervalInDay = intervalFlowData.filter(
        (item) => item.time > time && item.time <= nextDay,
      )
      const totalFlowInDay = allIntervalInDay.reduce((sum, { index }) => sum + index, 0)

      items = [
        mapToItem({
          type: "total",
          index: totalFlowInDay,
          time,
        }),
      ]
    }

    let addedTime = 0

    switch (viewMode) {
      case "hourly":
        addedTime = 24 * 3600 * 1000
        break
      case "daily":
      case "weekly":
        addedTime = 24 * 3600 * 1000
        break
    }

    return items.map((item, i, arr) => {
      const startTime = item.time
      const endTime = i < arr.length - 1 ? arr[i + 1]!.time : item.time + addedTime
      return [startTime, endTime, item.index] as [number, number, number]
    })
  }, [intervalFlowData, selectedDate, selectedMonth, selectedWeek, viewMode])

  const maxTotalFlow = useMemo(
    () => getEchartsMax(totalFlowChartData.reduce((max, item) => Math.max(max, item[2]), 0)),
    [totalFlowChartData],
  )

  const maxIntervalFlow = useMemo(() => {
    return getEchartsMax(Math.max(...processedIntervalFlowData.map((item) => item[1])))
  }, [processedIntervalFlowData])

  const result = useMemo(
    () => ({
      intervalFlowData: processedIntervalFlowData,
      totalFlowChartData,
      maxIntervalFlow,
      maxTotalFlow,
    }),
    [processedIntervalFlowData, totalFlowChartData, maxIntervalFlow, maxTotalFlow],
  )

  const resultString = JSON.stringify(result)
  return useMemo(() => JSON.parse(resultString) as ChartData, [resultString])
}
